import { cn } from "@/common/utils/cn"
import { VariantProps } from "class-variance-authority"
import { buttonVariants } from "./styles"
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react"
import Spinner from "../Spinner";
import useUserPermissions from "../../hooks/useUserPermissions";
import { Tooltip, type PlacesType } from "react-tooltip";
import Skeleton from "../Skeleton";

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  disabled?: boolean
  name?: string
  type?: "button" | "submit" | "reset"
  isLoading?: boolean
  spinnerClassName?: string
  spinnerWrapperClassName?: string;
  tooltipPlace?: PlacesType;
  tooltipMessage?: string;
  noPermissionText?: string
  operationKlassToCheckPermission?: string
  propertiesToCheckPermission?: string[];
  noPermissionBehavior?: 'disable' | 'hide';
  dataTestId?: string;
  disabledClassName?: string;
  hideContentOnLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant,
  name,
  className,
  disabled = false,
  isLoading = false,
  type = 'button',
  spinnerClassName,
  spinnerWrapperClassName,
  tooltipPlace = "left",
  tooltipMessage,
  noPermissionText = 'Você não tem permissão',
  operationKlassToCheckPermission,
  propertiesToCheckPermission,
  noPermissionBehavior = 'disable',
  dataTestId,
  disabledClassName,
  style,
  hideContentOnLoading,
  ...props
}, ref) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [buttonWidth, setButtonWidth] = useState<number>();
  const [buttonHeight, setButtonHeight] = useState<number>();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const tooltipId = useId();

  const isPermissionNeeded = !!operationKlassToCheckPermission;
  const hasPermission = isPermissionNeeded && hasUserPermission(operationKlassToCheckPermission, propertiesToCheckPermission);

  useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

  useEffect(() => {
    if (hideContentOnLoading && buttonRef.current) {
      const buttonWidth = buttonRef.current.getBoundingClientRect();

      setButtonWidth(buttonWidth.width);
      setButtonHeight(buttonWidth.height);
    }
  }, [!!buttonRef.current, hasPermission]);

  if (isPermissionNeeded && noPermissionBehavior === 'hide') {
    if (isUserPermissionsPending) {
      return (
        <Skeleton
          className={cn(
            'py-2 px-4 rounded-sm',
            className,
            'bg-white-100',
          )}
        />
      )
    }

    if (!hasPermission) {
      return null;
    }
  }

  return (
    <>
      <button
        className={cn(
          buttonVariants({ variant }),
          className,
          (!!disabled || isLoading || (isPermissionNeeded && !hasPermission))
            ? (disabledClassName || 'opacity-50 cursor-default')
            : ''
        )}
        style={hideContentOnLoading && isLoading && buttonWidth && buttonHeight && {
          width: buttonWidth,
          height: buttonHeight,
        } || undefined}
        data-testid={dataTestId || (name + '_button')}
        data-tooltip-id={((isPermissionNeeded && !hasPermission) || tooltipMessage) ? tooltipId : ''}
        type={type}
        ref={buttonRef}
        {...props}
        disabled={disabled || (isPermissionNeeded && !hasPermission)}
      >
        {(!hideContentOnLoading || !isLoading) && children}

        {(isLoading || (isPermissionNeeded && isUserPermissionsPending)) && (
          <div className={spinnerWrapperClassName}>
            <Spinner className={cn("w-4 h-4 fill-white-800", spinnerClassName)} />
          </div>
        )}
      </button>

      {((isPermissionNeeded && !hasPermission && !isUserPermissionsPending) || tooltipMessage) && (
        <Tooltip id={tooltipId} place={tooltipPlace}>{tooltipMessage || noPermissionText}</Tooltip>
      )}
    </>
  );
});

Button.displayName = "Button"

export default Button
